import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useStripe } from '@stripe/react-stripe-js';
import type { PaymentRequest, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { debounce } from 'es-toolkit/compat';

import { useUpgradeContext } from '~/context/useUpgradeContext';
import { PriceInterval } from '~/interfaces/subscriptions';

import { generateInitialPaymentRequest } from './generateInitialPaymentRequest';

interface Props {
  shouldCreatePaymentRequest: boolean;
}

interface ReturnProps {
  paymentRequest: any;
  applePayEvent: any;
  setApplePayEvent: (event: any) => void;
  onPaymentRequestChange: (
    e: PaymentRequestPaymentMethodEvent,
    fetcher: any,
    formElement: HTMLFormElement,
  ) => Promise<void>;
}

const useApplePay = ({ shouldCreatePaymentRequest = false }: Props): ReturnProps => {
  const stripe = useStripe();
  const { selectedPrice, selectedGiftPrice, currentFormMode, donationAmountCents } = useUpgradeContext();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [applePayEvent, setApplePayEvent] = useState<PaymentRequestPaymentMethodEvent | null>(null);

  const evaluatedSelectedPrice = currentFormMode === 'gift' ? selectedGiftPrice : selectedPrice;

  // Sets the initial Apple Pay payment request
  useEffect(() => {
    if (stripe && shouldCreatePaymentRequest && evaluatedSelectedPrice) {
      const { currency, label, amount } = generateInitialPaymentRequest(
        evaluatedSelectedPrice,
        Number(donationAmountCents) || 0,
      );

      const pr = stripe.paymentRequest({
        country: 'US',
        currency,
        total: {
          label,
          amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
        disableWallets: ['googlePay', 'browserCard'],
      });

      pr.canMakePayment()
        .then((result: any) => {
          if (result) {
            setPaymentRequest(pr);
          }
        })
        .catch((err: any) => {
          console.log(err);
        });
    }
  }, [stripe, shouldCreatePaymentRequest]);

  const updatePaymentRequest = (newAmount: number, cta?: string) => {
    paymentRequest?.update({
      total: {
        label: cta || 'Pay now',
        amount: newAmount,
      },
    });
  };

  const debouncedUpdatePaymentRequest = debounce(updatePaymentRequest, 100);

  // Handle event updates
  useEffect(() => {
    if (paymentRequest) {
      if (evaluatedSelectedPrice?.interval === PriceInterval.DONATION) {
        try {
          updatePaymentRequest(Number(donationAmountCents) || 0, evaluatedSelectedPrice.cta);
        } catch (e) {
          console.log(e);
        }
      } else {
        updatePaymentRequest(evaluatedSelectedPrice?.amount_cents || 0, evaluatedSelectedPrice?.cta);
      }
    }
  }, [evaluatedSelectedPrice]);

  // Handle donation amount changes
  // Use debounced method to update payment request
  // as price can change frequently when the user is
  // typing in the price input
  useEffect(() => {
    if (paymentRequest) {
      if (evaluatedSelectedPrice?.interval === PriceInterval.DONATION) {
        try {
          debouncedUpdatePaymentRequest(Number(donationAmountCents) || 0, evaluatedSelectedPrice?.cta);
        } catch (e) {
          console.log(e);
        }
      }
    }
  }, [donationAmountCents]);

  const handlePaymentRequestChange = async (
    event: PaymentRequestPaymentMethodEvent,
    fetcher: any,
    formElement: HTMLFormElement,
  ) => {
    const paymentMethodId = event?.paymentMethod?.id || '';

    if (!paymentMethodId) {
      toast.error('Something went wrong while trying to create a payment request, please try again');
      return;
    }

    setApplePayEvent(event);

    const formData = new FormData(formElement);
    formData.append('payment_method', paymentMethodId);
    fetcher.submit(formData, { method: 'post', action: '/upgrade' });
  };

  return {
    paymentRequest,
    applePayEvent,
    setApplePayEvent,
    onPaymentRequestChange: handlePaymentRequestChange,
  };
};

export default useApplePay;
